import { useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Mail, Phone, Calendar, MessageSquare, Briefcase, AlertTriangle, Handshake, FileText,
  Paperclip, ExternalLink, Plus, Loader2, Check, UtensilsCrossed, MailOpen, QrCode,
} from "lucide-react";
import { LOG_OPTIONS, LOG_LABEL, activityTypeFor, isLogOption, type LogOption } from "./logOptions";
import { collapseSyncedEmails } from "./emailDedupe";
import { EmailViewerDialog } from "./EmailViewerDialog";
import { senderLabel, recipientLabel } from "./emailParties";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { occurredAtFromLocalDate } from "@shared/activityTime";
import { ENGAGEMENT_ACTIVITY_TYPES } from "@shared/engagement";

/**
 * The one activity timeline, on every detail page.
 *
 * A company page shows everything logged against the company. A person page
 * shows what involves that person: logged on them, ticked in "Who was there",
 * a calendar meeting they attended, or an email carrying their address. Both
 * endpoints return the same row shapes, so everything below is scope-blind
 * except the fetch and the save body.
 */

export type ActivityScope =
  | { kind: "company"; companyId: string; companyName: string; contacts?: { id: string; name: string }[] }
  | { kind: "contact"; contactId: string; contactName: string };

interface Person { id: string; name: string }

interface ActivityRow {
  id: string; type: string; title: string; content: string | null;
  date: string; userName: string | null; people?: Person[];
  metadata?: { syncedBy?: string } | null;
  meal?: boolean;
}

interface EmailRow {
  id: string; type: "synced" | "logged"; subject: string | null;
  preview: string | null; from: any; to: any; date: string;
  hasAttachments: boolean; webLink: string | null; userName: string | null;
}

const ICON: Record<string, any> = {
  email: Mail, call: Phone, meeting: Calendar, note: MessageSquare, conference: Briefcase,
  stage_change: AlertTriangle, referral_sent: Handshake, document: FileText, qr_scan: QrCode,
};
const COLOR: Record<string, string> = {
  email: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  call: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  meeting: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  note: "bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400",
  conference: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  stage_change: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  referral_sent: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  qr_scan: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
};

export const activityKeys = {
  company: (id: string) => ["company-activities", id] as const,
  companyEmails: (id: string) => ["company-emails", id] as const,
  contact: (id: string) => ["contact-activity-feed", id] as const,
};

/**
 * After any activity write. One row can surface on a company timeline and on
 * several person timelines at once, so refresh by prefix rather than guessing
 * which ones are on screen; only mounted queries actually refetch.
 */
export function invalidateActivityViews(qc: QueryClient) {
  for (const key of [["company-activities"], ["contact-activity-feed"], ["company"], ["contact"], ["coi"], ["cois"], ["coi-bd"], ["/api/dashboard/origination"], ["record-summary"]]) {
    qc.invalidateQueries({ queryKey: key });
  }
}

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error("Failed to load activity");
  return r.json();
}

function useTimelineData(scope: ActivityScope) {
  const companyId = scope.kind === "company" ? scope.companyId : null;
  const contactId = scope.kind === "contact" ? scope.contactId : null;

  const companyActs = useQuery<ActivityRow[]>({
    queryKey: activityKeys.company(companyId ?? ""),
    queryFn: () => getJson(`/api/companies/${companyId}/activities`),
    enabled: !!companyId,
  });
  const companyEmails = useQuery<EmailRow[]>({
    queryKey: activityKeys.companyEmails(companyId ?? ""),
    queryFn: () => getJson(`/api/companies/${companyId}/emails`),
    enabled: !!companyId,
  });
  const person = useQuery<{ activities: ActivityRow[]; emails: EmailRow[] }>({
    queryKey: activityKeys.contact(contactId ?? ""),
    queryFn: () => getJson(`/api/contacts/${contactId}/activity-feed`),
    enabled: !!contactId,
  });

  if (companyId) {
    return {
      activities: companyActs.data ?? [], emails: companyEmails.data ?? [],
      isLoading: companyActs.isLoading, isError: companyActs.isError,
    };
  }
  return {
    activities: person.data?.activities ?? [], emails: person.data?.emails ?? [],
    isLoading: person.isLoading, isError: person.isError,
  };
}

type Item =
  | (ActivityRow & { _kind: "activity"; _date: Date })
  | (EmailRow & { _kind: "email"; _date: Date; title: string; content: string | null });

function mergeItems(activitiesIn: ActivityRow[], emails: EmailRow[]): Item[] {
  // Calendar sync writes one row per FTC mailbox that holds the event, so a
  // meeting three of us attended arrives three times. Same type, title and
  // start from calendar sync is one meeting.
  const seenMeeting = new Set<string>();
  const activities = activitiesIn.filter((a) => {
    if (!a.metadata?.syncedBy) return true;
    const k = `${a.type}|${a.title}|${a.date}`;
    if (seenMeeting.has(k)) return false;
    seenMeeting.add(k);
    return true;
  });
  const synced = collapseSyncedEmails(emails);
  return [
    ...activities.map((a) => ({ ...a, _kind: "activity" as const, _date: new Date(a.date) })),
    ...synced.map((e) => ({ ...e, _kind: "email" as const, _date: new Date(e.date), title: e.subject ?? "", content: e.preview })),
  ].sort((a, b) => b._date.getTime() - a._date.getTime());
}

function groupByPeriod(items: Item[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const monthAgo = new Date(today.getTime() - 30 * 86400000);
  return [
    { label: "Today", items: items.filter((m) => m._date >= today) },
    { label: "This Week", items: items.filter((m) => m._date >= weekAgo && m._date < today) },
    { label: "This Month", items: items.filter((m) => m._date >= monthAgo && m._date < weekAgo) },
    { label: "Older", items: items.filter((m) => m._date < monthAgo) },
  ].filter((g) => g.items.length > 0);
}

interface Props {
  scope: ActivityScope;
  /**
   * Open the form from outside (a header "Log call" button). Pass the type to
   * open, null when closed. Leave both unset to let the component own it.
   */
  compose?: string | null;
  onComposeChange?: (type: string | null) => void;
  className?: string;
}

export function ActivityTimeline({ scope, compose: composeProp, onComposeChange, className }: Props) {
  const queryClient = useQueryClient();
  const [composeLocal, setComposeLocal] = useState<string | null>(null);
  const controlled = composeProp !== undefined;
  const compose = controlled ? composeProp : composeLocal;
  const setCompose = (t: string | null) => {
    if (!controlled) setComposeLocal(t);
    onComposeChange?.(t);
  };

  const { activities, emails, isLoading, isError } = useTimelineData(scope);
  const groups = groupByPeriod(mergeItems(activities, emails));
  const selfId = scope.kind === "contact" ? scope.contactId : null;

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Activity Timeline</h3>
        {!compose && (
          <Button size="sm" className="gap-1.5" onClick={() => setCompose("note")}>
            <Plus className="w-3.5 h-3.5" /> Log Activity
          </Button>
        )}
      </div>

      {compose && (
        <LogActivityForm
          // Remount per requested type so a header "Log call" resets the form.
          key={compose}
          scope={scope}
          initialType={compose}
          onDone={() => setCompose(null)}
          onSaved={() => invalidateActivityViews(queryClient)}
        />
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive py-6">Couldn't load activity. Refresh to try again.</p>
      ) : groups.length === 0 ? (
        !compose && (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No activity yet</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setCompose("note")}>Log an activity</Button>
          </div>
        )
      ) : (
        groups.map((group) => (
          <div key={group.label}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group.label}</p>
            <div className="space-y-1">
              {group.items.map((item) => <TimelineRow key={`${item._kind}:${item.id}`} item={item} selfId={selfId} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TimelineRow({ item, selfId }: { item: Item; selfId: string | null }) {
  const type = item._kind === "email" ? "email" : item.type;
  const meal = item._kind === "activity" && !!item.meal;
  const Icon = meal ? UtensilsCrossed : ICON[type] || MessageSquare;
  // On a person's page, "with" lists everyone else on it.
  const others = item._kind === "activity" ? (item.people ?? []).filter((p) => p.id !== selfId) : [];
  const [viewing, setViewing] = useState(false);
  // An email row names its sender. userName on a synced email is only the
  // teammate whose mailbox held the copy.
  const who = (item._kind === "email" ? senderLabel(item.from) : null) || item.userName || "System";
  const to = item._kind === "email" ? recipientLabel(item.to) : null;
  return (
    <div className="flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
      <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0", COLOR[type] || COLOR.note)}>
        <Icon className="w-3.5 h-3.5" />
        {meal && <span className="sr-only">Meal</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{who}</span>
          {to && <span className="text-xs text-muted-foreground truncate">→ {to}</span>}
          <span className="text-xs text-muted-foreground ml-auto shrink-0">{item._date.toLocaleDateString()}</span>
        </div>
        {item.title && <p className="text-sm mt-0.5 truncate">{item.title}</p>}
        {others.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            with{" "}
            {others.map((p, i) => (
              <span key={p.id}>
                {i > 0 && ", "}
                <a href={`/contacts/${p.id}`} className="hover:text-primary-text hover:underline">{p.name}</a>
              </span>
            ))}
          </p>
        )}
        {item.content && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-line">{item.content}</p>}
        {item._kind === "email" && (
          <div className="flex items-center gap-2 mt-1">
            {item.hasAttachments && <Paperclip className="w-3 h-3 text-muted-foreground" />}
            {item.webLink ? (
              <a href={item.webLink} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5">
                <ExternalLink className="w-2.5 h-2.5" />Outlook
              </a>
            ) : (
              // A teammate's copy: Outlook can only open the viewer's own mailbox.
              <button
                type="button"
                onClick={() => setViewing(true)}
                title={item.userName ? `In ${item.userName}'s mailbox` : undefined}
                className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5"
              >
                <MailOpen className="w-2.5 h-2.5" />Open
              </button>
            )}
          </div>
        )}
        {item._kind === "email" && viewing && (
          <EmailViewerDialog id={item.id} open={viewing} onOpenChange={setViewing} />
        )}
      </div>
    </div>
  );
}

function LogActivityForm({ scope, initialType, onDone, onSaved }: {
  scope: ActivityScope; initialType: string; onDone: () => void; onSaved: () => void;
}) {
  const [option, setOption] = useState<LogOption>(isLogOption(initialType) ? initialType : "note");
  const { type, meal } = activityTypeFor(option);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState(todayYmd());
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const people = scope.kind === "company" ? scope.contacts ?? [] : [];
  const isConversation = (ENGAGEMENT_ACTIVITY_TYPES as readonly string[]).includes(type);

  // A call with no notes is still a real log (it earns the checkmark); a note with no text is nothing.
  const canSave = isConversation || !!content.trim() || !!title.trim();

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // A blank title still says who it was with, so the feed reads without opening anything.
  const defaultTitle = () => {
    if (!isConversation) return LOG_LABEL[option];
    const names = scope.kind === "contact"
      ? [scope.contactName]
      : people.filter((p) => picked.includes(p.id)).map((p) => p.name);
    const withWhom = names.length ? names.join(", ") : scope.kind === "company" ? scope.companyName : "";
    return withWhom ? `${LOG_LABEL[option]} with ${withWhom}` : LOG_LABEL[option];
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        type, ...(meal ? { meal: true } : {}),
        title: title.trim() || defaultTitle(),
        content: content.trim(),
        occurredAt: occurredAtFromLocalDate(date),
        ...(scope.kind === "company"
          ? { companyId: scope.companyId, contactIds: picked }
          : { contactIds: [scope.contactId] }),
      };
      const res = await fetch("/api/activities", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Couldn't log the activity");
      }
      onSaved();
      toast.success("Activity logged");
      onDone();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-primary/30">
      <CardContent className="p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {LOG_OPTIONS.map((t) => (
            <button key={t} type="button" onClick={() => setOption(t)}
              className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors",
                option === t ? "border-primary bg-primary/5 text-primary-text" : "border-border")}>
              {LOG_LABEL[t]}
            </button>
          ))}
        </div>

        {people.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">{isConversation ? "Who was there" : "About"} <span className="font-normal">(optional)</span></p>
            <div className="flex gap-1.5 flex-wrap">
              {people.map((p) => {
                const on = picked.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggle(p.id)} aria-pressed={on}
                    className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs transition-colors",
                      on ? "border-primary bg-primary/10 text-primary-text" : "border-border text-muted-foreground hover:text-foreground")}>
                    {on && <Check className="w-3 h-3" />}{p.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={defaultTitle()} className="h-8 text-sm" />
        <textarea
          className="w-full min-h-[80px] p-2 border rounded bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={content} onChange={(e) => setContent(e.target.value)} placeholder="Details..." autoFocus
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Input type="date" value={date} max={todayYmd()} onChange={(e) => setDate(e.target.value)} className="h-8 text-sm w-40" />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onDone} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !canSave}>
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
